import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PencilSimple, FloppyDisk, Watch, DoorOpen, WifiHigh, Thermometer, Pulse } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useOrg } from '../../contexts/OrgContext';
import { adminListDeviceTypes, adminUpdateDeviceTypeLabel, formatAdminError } from '../../lib/admin';
import { invalidateDeviceCatalog, categoryLabel } from '../../lib/deviceCatalog';
import AdminPage from '../../components/admin/AdminPage';
import { Field, inputCls, Modal } from '../../components/admin/Field';

const CATEGORY_ICONS = { bracelet: Watch, door_sensor: DoorOpen, gateway: WifiHigh, sensor: Thermometer };
const iconFor = (cat) => CATEGORY_ICONS[cat] || Pulse;

function Badge({ on, onText, offText }) {
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full ${
      on ? 'bg-sage-100 text-sage-700' : 'bg-stone_line text-ink-soft'
    }`}>
      {on ? onText : offText}
    </span>
  );
}

export default function AdminDeviceTypes() {
  const { t } = useI18n();
  const { isSuperAdmin } = useOrg();

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // the type row being renamed

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await adminListDeviceTypes());
    } catch (err) {
      toast.error(formatAdminError(t('admin_dt_load_failed'), err));
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { reload(); }, [reload]);

  const onSaved = (typeValue, label) => {
    setTypes((prev) => prev.map((d) => (d.value === typeValue ? { ...d, label } : d)));
    invalidateDeviceCatalog();
    setEditing(null);
  };

  return (
    <AdminPage title={t('admin_dt_title')} description={t('admin_dt_sub')}>

      {!isSuperAdmin && (
        <p className="text-xs text-ink-muted mb-4" data-testid="admin-dt-readonly-hint">
          {t('admin_dt_readonly_hint')}
        </p>
      )}

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : types.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">{t('no_results')}</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-device-types-table">
            <thead className="bg-bg-alt text-ink-soft text-left text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-5 py-3 font-medium">{t('admin_dt_col_label')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_dt_col_key')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_dt_col_category')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_dt_col_wearable')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_dt_col_ble_sync')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_dt_col_ble_type_id')}</th>
                <th className="px-5 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone_line">
              {types.map((d) => {
                const Icon = iconFor(d.category);
                return (
                  <tr key={d.value} className="hover:bg-bg-alt/40" data-testid={`admin-dt-row-${d.value}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-sage-100 text-sage-600 grid place-items-center shrink-0">
                          <Icon size={16} weight="duotone" />
                        </div>
                        <span className="font-medium text-ink" data-testid={`admin-dt-label-${d.value}`}>{d.label}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-soft font-mono text-xs">{d.value}</td>
                    <td className="px-5 py-3 text-ink-soft text-xs">{categoryLabel(d.category)}</td>
                    <td className="px-5 py-3">
                      <Badge on={!!d.wearable} onText={t('admin_dt_wearable_yes')} offText={t('admin_dt_wearable_no')} />
                    </td>
                    <td className="px-5 py-3">
                      <Badge on={!!d.ble_sync} onText={t('admin_dt_ble_sync_on')} offText={t('admin_dt_ble_sync_off')} />
                    </td>
                    <td className="px-5 py-3 text-ink-soft font-mono text-xs">{d.ble_type_id ?? '—'}</td>
                    <td className="px-5 py-3 text-right">
                      {isSuperAdmin && (
                        <button
                          onClick={() => setEditing(d)}
                          data-testid={`admin-dt-edit-${d.value}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt hover:text-ink"
                          aria-label={t('admin_dt_edit_label')} title={t('admin_dt_edit_label')}
                        >
                          <PencilSimple size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <EditLabelModal
          row={editing}
          onCancel={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </AdminPage>
  );
}

function EditLabelModal({ row, onCancel, onSaved }) {
  const { t } = useI18n();
  const [label, setLabel] = useState(row.label || '');
  const [saving, setSaving] = useState(false);

  const dirty = label.trim() && label.trim() !== (row.label || '');

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = label.trim();
    if (!next) { toast.error(t('admin_dt_label_required')); return; }
    if (saving) return;
    setSaving(true);
    try {
      const res = await adminUpdateDeviceTypeLabel(row.value, next);
      toast.success(t('admin_dt_label_saved'));
      onSaved(row.value, res?.label ?? next);
    } catch (err) {
      toast.error(formatAdminError(t('admin_dt_save_failed'), err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onCancel={onCancel}>
      <form onSubmit={onSubmit} data-testid="admin-dt-edit-form" className="space-y-4">
        <div>
          <h2 className="font-heading text-2xl text-ink leading-tight">{t('admin_dt_edit_title')}</h2>
          <p className="text-sm text-ink-soft mt-1">{t('admin_dt_edit_desc', { key: row.value })}</p>
        </div>
        <Field label={t('admin_dt_col_label')} required>
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            data-testid="admin-dt-label-input"
            className={inputCls}
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={saving}
            data-testid="admin-dt-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50">
            {t('cancel')}
          </button>
          <button type="submit" disabled={!dirty || saving}
            data-testid="admin-dt-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50 transition-colors">
            <FloppyDisk size={16} weight="bold" />
            {saving ? '…' : t('save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
